import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Download as DownloadIcon,
  OfflinePin as OfflinePinIcon,
  OfflinePinOutlined as OfflinePinOutlinedIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { fetcher } from '../../utils/api';
import { apiBaseURL } from '../../constants/routes';
import { useAuthProvider } from '../../utils/auth';
import { useOfflineTrack } from '../../hooks/useOfflineTrack';
import { OFFLINE_CHANGED, listSaved, offlineSupported } from '../../utils/offlineLibrary';
import SongCardListMenu from '../../components/SongCardListMenu';
import SaveFileDialog from '../../components/SaveFileDialog';

// Purchased music: everything you've bought, which of it is kept on this
// device for offline listening, and the file itself to save.

const formatSize = (bytes) => (bytes > 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : `${Math.round(bytes / 1e6)} MB`);

const OfflineToggle = ({ track }) => {
  const offline = useOfflineTrack(track);
  if (!offline.supported) return null;
  if (offline.saving) return <CircularProgress size={18} sx={{ mx: 1 }} />;
  return (
    <Tooltip title={offline.error || (offline.saved ? 'Kept on this device - tap to remove' : 'Keep on this device for offline listening')}>
      <IconButton
        size="small"
        onClick={offline.saved ? offline.remove : offline.save}
        aria-label={offline.saved ? `Remove offline copy of ${track.name}` : `Keep ${track.name} for offline listening`}
        color={offline.error ? 'error' : offline.saved ? 'primary' : 'default'}
      >
        {offline.saved ? <OfflinePinIcon fontSize="small" /> : <OfflinePinOutlinedIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const SaveFileButton = ({ track }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title="Save the file">
        <IconButton size="small" onClick={() => setOpen(true)} aria-label={`Save the file for ${track.name}`}>
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {open && <SaveFileDialog open onClose={() => setOpen(false)} track={track} />}
    </>
  );
};

const Downloads = () => {
  const { user } = useAuthProvider();
  const [saved, setSaved] = useState(() => listSaved(user?.uuid));
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    const refresh = () => setSaved(listSaved(user?.uuid));
    const connection = () => setOnline(navigator.onLine);
    refresh();
    window.addEventListener(OFFLINE_CHANGED, refresh);
    window.addEventListener('online', connection);
    window.addEventListener('offline', connection);
    return () => {
      window.removeEventListener(OFFLINE_CHANGED, refresh);
      window.removeEventListener('online', connection);
      window.removeEventListener('offline', connection);
    };
  }, [user?.uuid]);

  const { data, isLoading, error } = useQuery(
    ['userDownloads'],
    () => fetcher(`${apiBaseURL}/music/downloads/user/?page_size=200`),
    { enabled: online }
  );
  const owned = (data?.downloads || [])
    .filter((download) => download.is_permanent_purchase)
    .map((download) => ({ ...download.track, act_name: download.track.acts }));

  // Offline, the saved copies are all there is to play
  const tracks = online ? owned : saved;
  // Music kept offline that they haven't bought (charged when played offline)
  const ownedIds = new Set(owned.map((track) => track.id));
  const otherKept = online ? saved.filter((entry) => !entry.owned && !ownedIds.has(entry.id)) : [];
  const savedBytes = saved.reduce((total, entry) => total + (entry.size || 0), 0);

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>Purchased Music</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Music you've bought streams free, forever. Keep it on this device to listen offline
        <OfflinePinOutlinedIcon fontSize="inherit" sx={{ mx: 0.5, verticalAlign: 'middle' }} />
        or save the file itself
        <DownloadIcon fontSize="inherit" sx={{ mx: 0.5, verticalAlign: 'middle' }} />.
      </Typography>

      {!offlineSupported() && (
        <Alert severity="info" sx={{ mb: 2 }}>This browser can't keep music for offline listening.</Alert>
      )}
      {!online && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You're offline. Music kept on this device still plays, and your plays are sent when you're back online.
        </Alert>
      )}
      {saved.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {saved.length} track{saved.length === 1 ? '' : 's'} kept on this device ({formatSize(savedBytes)}).
          Your browser may clear them if it runs low on space.
        </Typography>
      )}

      {online && isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : online && error ? (
        <Alert severity="error">Couldn't load your music. Please try again.</Alert>
      ) : tracks.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary">
            {online ? "You haven't bought any music yet" : 'Nothing is kept on this device yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Buy a track or album from its menu to own it.
          </Typography>
        </Box>
      ) : (
        tracks.map((track, index) => (
          <SongCardListMenu
            key={track.id}
            track={track}
            index={index + 1}
            albumTracks={tracks}
            showArtwork
            actions={online ? <><OfflineToggle track={track} /><SaveFileButton track={track} /></> : null}
          />
        ))
      )}

      {otherKept.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Also kept on this device</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Music you haven't bought. Plays while you're offline are charged at your usual rate when you reconnect.
          </Typography>
          {otherKept.map((track, index) => (
            <SongCardListMenu
              key={track.id}
              track={track}
              index={index + 1}
              albumTracks={otherKept}
              showArtwork
              actions={<OfflineToggle track={track} />}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Downloads;
